"use client";

import { useState, useRef } from "react";
import { Box, Avatar, CircularProgress, Link } from "@mui/material";
import {
  Dialog,
  DialogPortal,
  DialogBackdrop,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
  DialogClose,
  Button,
  ConfirmButton,
} from "@/ui";
import TextField from "@components/shared/form/TextField";
import RichTextAIField, { isHtmlEmpty } from "@components/shared/form/ai/RichTextAIField/RichTextAIField";
import { createRecruitingPrompt } from "@/lib/agent/utils/candidateToPrompt";
import FormSection from "./parts/FormSection";
import FormFieldTitle from "./parts/FormFieldTitle";

interface Project {
  id?: number | string;
  name?: string | null;
  role?: string | null;
  description?: string | null;
  image?: string | null;
  [key: string]: unknown;
}

interface EditProjectsProps {
  onClose: () => void;
  open: boolean;
  project?: Project | null;
  onUpdate: (project: Record<string, unknown>) => void;
  onDelete: (id: Project["id"]) => void;
  candidate: unknown;
}

export default function EditProjects({ onClose, open, project, onUpdate, onDelete, candidate }: EditProjectsProps) {
  const [projectData, setProjectData] = useState({
    name: project?.name || "",
    role: project?.role || "",
    description: project?.description || "",
    image: project?.image || "",
    image_data: null as File | null,
  });
  const [imageLoading, setImageLoading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setProjectData({ ...projectData, [event.target.name]: event.target.value });
  };

  const handleSubmit = () => {
    // The same rich-text editor the float builder uses; a cleared editor
    // leaves "<p></p>" behind, which is saved as empty.
    const description = isHtmlEmpty(projectData.description) ? "" : projectData.description;
    onUpdate({ ...project, ...projectData, description });
    onClose();
  };

  const handleUploadImageClick = (event: React.MouseEvent) => {
    event.preventDefault();
    fileInputRef.current?.click();
  };

  const handleImageRemove = (event: React.MouseEvent) => {
    event.preventDefault();
    setProjectData((prev) => ({ ...prev, image: "", image_data: null }));
  };

  const handleUpdateImage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setImageLoading(true);
    const reader = new FileReader();
    reader.onloadend = () => {
      setProjectData((prev) => ({ ...prev, image: String(reader.result ?? ""), image_data: file }));
      setImageLoading(false);
    };
    reader.onerror = () => setImageLoading(false);
    reader.readAsDataURL(file);
  };

  const handleDeleteProject = () => {
    onDelete(project?.id);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogPortal>
        <DialogBackdrop />
        <DialogContent size="lg">
          <DialogHeader>
            <DialogTitle>Edit project details</DialogTitle>
            <DialogClose />
          </DialogHeader>
          <DialogBody>
            <FormSection>
              <FormFieldTitle>Project name:</FormFieldTitle>
              <TextField fullWidth name="name" variant="outlined" value={projectData.name} onChange={handleChange} />
            </FormSection>
            <FormSection>
              <FormFieldTitle>Your role:</FormFieldTitle>
              <TextField fullWidth name="role" variant="outlined" value={projectData.role} onChange={handleChange} />
            </FormSection>
            <FormSection>
              <FormFieldTitle>Description:</FormFieldTitle>
              <RichTextAIField
                id="project-description"
                value={projectData.description}
                onChange={(value) => setProjectData((prev) => ({ ...prev, description: value }))}
                placeholder="Describe this project and your contributions..."
                alwaysShowToolbar
                minHeight={96}
                prompt={
                  createRecruitingPrompt(candidate, "general") +
                  `. This is a project description for "${projectData.name || "a project"}" where the candidate's role was "${projectData.role || "contributor"}". Write a compelling description that highlights the project's scope, technologies used, the candidate's specific contributions, and the impact or outcomes achieved. Focus on quantifiable results and technical/professional growth. Make it suitable for recruiting presentations.`
                }
              />
            </FormSection>
            <FormSection sx={{ flexDirection: "row", display: "flex", gap: "16px" }}>
              <FormFieldTitle>Project logo:</FormFieldTitle>
              <Box sx={{ display: "flex", flexDirection: "column", gap: "16px", alignItems: "center" }}>
                {!imageLoading && (
                  <Avatar src={projectData.image} alt={projectData.name} sx={{ width: 84, height: 84, fontSize: "32px", fontWeight: 600, backgroundColor: "#e0e7ff", color: "var(--primitive-indigo-600, #13319a)" }}>
                    {projectData.name.substring(0, 2)}
                  </Avatar>
                )}
                {imageLoading && <CircularProgress size={80} color="secondary" />}
                {!imageLoading && !projectData.image && (
                  <Link href="#" onClick={handleUploadImageClick}>Upload image</Link>
                )}
                {!imageLoading && projectData.image && (
                  <Link href="#" onClick={handleImageRemove}>Remove</Link>
                )}
                <input hidden accept="image/*" type="file" onChange={handleUpdateImage} ref={fileInputRef} />
              </Box>
            </FormSection>
          </DialogBody>
          <DialogFooter style={{ justifyContent: "space-between" }}>
            <ConfirmButton
              text="Delete project"
              onUpdate={handleDeleteProject}
              variant="tertiary"
              color="red"
            />
            <div style={{ display: "flex", gap: 8 }}>
              <Button variant="secondary" onClick={onClose}>Cancel</Button>
              <Button variant="primary" onClick={handleSubmit}>Save changes</Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </DialogPortal>
    </Dialog>
  );
}
